// Plan 24a, Task 2.7 — Character Select screen (Screen 6)
//
// Three-panel layout:
//   - Title "CHOOSE YOUR LOOK" (centered, h1, white, title shadow)
//   - LEFT panel: 10 circular color swatches (32px) with shape-icon glyphs
//   - CENTER panel: <canvas> host for the 3D preview rig (built via adapter)
//   - RIGHT panel: HEAD (2x4) + EYES (2x4) + CAPE (row of 9 circles)
//   - BOTTOM: READY button (Primary, Large)
//
// The screen NEVER builds the 3D rig itself — it consumes Plan 24a Task 1.2's
// CharacterPreviewAdapter façade. The adapter returns a CharacterPreviewRig
// with applyCustomization() that the screen invokes on every UI change.
//
// Render-loop ownership: Plan 19's main loop animates the rig.root. This screen
// only mounts/unmounts the rig and forwards customization updates.
//
// Design system references:
//   - docs/design/tokens.json — color.player.*, typography.shadow.title,
//     border.focus-gamepad (#E8A020 amber), color.text.primary, spacing.*,
//     border-radius.circle/pill, color.ui.panel-bg-alpha
//   - docs/design/components.md — Button §1, Panel §3
//   - docs/design/screen-details/pre-game.md §Screen 6 (lines 334-413)

import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';
import type { ScreenHandle, ScreenMountContext, ScreenId } from './types';
import {
  COLOR_ID_TO_HEX,
  type CharacterCustomization,
  type CharacterPreviewRig,
  type CapeId,
  type ColorId,
  type EyeId,
  type HeadId,
} from '../../renderer/character';
import type { CharacterPreviewAdapter } from './character-preview-adapter';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface CharacterSelectPayload {
  /** Adapter façade — production: Plan 19 wires real createPreviewRig; tests: stub. */
  adapter: CharacterPreviewAdapter;
  /** Color ids already claimed by other lobby members (greyed out, not selectable). */
  takenColors: readonly ColorId[];
  /** Pre-selected customization (e.g. when re-entering Character Select). */
  initial?: CharacterCustomization;
  /** Current lobby ready state for the player this panel controls. */
  ready?: boolean;
  /** Optional player/room summary shown in inline lobby mode. */
  playerName?: string;
  roomCode?: string;
  /** Where to navigate after READY click. Default: 'lobby-host'. */
  nextScreenId?: ScreenId;
  /** Called on READY/UNREADY click with the next ready state. */
  onReady: (c: CharacterCustomization, isReady: boolean) => void;
  /**
   * Optional: fired on every selection change (color/head/eyes/cape) for live preview.
   * Use in lobby to broadcast customization immediately so other browsers see changes.
   */
  onSelect?: (c: CharacterCustomization) => void;
  /** Optional camera hint fired only for category changes that need a close character view. */
  onCustomizeCamera?: (view: CharacterCustomizeCameraView) => void;
  /**
   * Control layout. Mobile category mode is opt-in so warmup customization keeps
   * its existing full-panel behavior unless a caller asks for compact tabs.
   */
  layoutMode?: CharacterSelectLayoutMode;
  /**
   * Mount mode.
   * - 'standalone' (default): navigates to nextScreenId after onReady.
   * - 'inline': skips router.navigate — caller is responsible for disposing
   *   the returned ScreenHandle when done (e.g. inside onReady).
   */
  mode?: 'standalone' | 'inline';
}

export type CharacterCustomizeCameraView = 'front' | 'back';
export type CharacterCustomizeCameraRequest = {
  playerId: string;
  view: CharacterCustomizeCameraView;
} | null;
export type CharacterSelectLayoutMode = 'desktopRail' | 'mobileCategories';

// ─────────────────────────────────────────────────────────────────────────────
// Tokens (mirrored from docs/design/tokens.json)
// ─────────────────────────────────────────────────────────────────────────────

// typography.shadow.title
const TITLE_SHADOW = '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)';
// typography.size.h1
const FONT_SIZE_H1 = 'var(--typography-size-h1, 48px)';
// typography.family.headline
const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
// color.text.primary
const COLOR_TEXT_PRIMARY = '#FFFFFF';
// color.text.secondary
const COLOR_TEXT_SECONDARY = '#B0B8C8';
// color.brand.amber — focus / selected ring
const COLOR_AMBER = '#E8A020';
const COLOR_SUCCESS = '#5CAD40';
// color.ui.panel-bg — opaque so lobby rail controls read clearly over the 3D scene.
const PANEL_BG = '#1E2332';
// color.ui.panel-border
const PANEL_BORDER = '3px solid rgba(255, 255, 255, 0.18)';
const PANEL_SHADOW = '0 4px 12px rgba(0, 0, 0, 0.5)';
// border-radius.lg
const BORDER_RADIUS_LG = '12px';
// spacing.sm
const SPACING_SM = '8px';
// No breakpoint tokens currently exist in docs/design/tokens.json.
const BREAKPOINT_NARROW = '720px';
const BREAKPOINT_MEDIUM_MAX = '1279px';

function injectCharacterSelectResponsiveStyles(): void {
  const styleId = 'character-select-responsive-styles';
  if (document.getElementById(styleId)) return;

  const styleEl = document.createElement('style');
  styleEl.id = styleId;
  styleEl.textContent = `
    .screen-character-select {
      --character-right-col-gap: 16px;
      --character-section-padding: 16px;
      --character-section-gap: 12px;
      --character-head-button-min-width: 72px;
      --character-head-button-height: 84px;
      --character-head-image-width: 86px;
      --character-head-image-height: 66px;
      --character-eye-button-min-height: 64px;
      --character-eye-image-width: 68px;
      --character-eye-image-height: 42px;
      container-type: inline-size;
      height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .character-select__columns {
      display: flex;
      gap: 24px;
      align-items: flex-start;
      justify-content: center;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      padding: 0 16px;
      flex: 1 1 auto;
      min-height: 360px;
      min-width: 0;
    }

    .character-select__left-col {
      display: flex;
      flex: 0 0 220px;
      flex-direction: column;
      gap: 16px;
      min-width: 0;
    }

    .character-select__preview-col {
      display: flex;
      flex: 1 1 360px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-width: 260px;
      min-height: 360px;
      background: transparent;
      border: none;
      border-radius: ${BORDER_RADIUS_LG};
      overflow: hidden;
      position: relative;
    }

    .character-select__right-col {
      display: flex;
      flex: 0 0 280px;
      flex-direction: column;
      gap: var(--character-right-col-gap);
      min-width: 0;
      max-height: calc(100vh - 132px);
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    .character-select__section-panel {
      flex: 0 0 auto;
      min-height: max-content;
      overflow: visible;
      overscroll-behavior: contain;
    }

    .screen-character-select--inline {
      --character-right-col-gap: 10px;
      --character-section-padding: 12px;
      --character-section-gap: 10px;
      --character-head-button-min-width: 0;
      --character-head-button-height: 70px;
      --character-head-image-width: 72px;
      --character-head-image-height: 56px;
      --character-eye-button-min-height: 52px;
      --character-eye-image-width: 56px;
      --character-eye-image-height: 34px;
    }

    .screen-character-select--inline .character-select__right-col {
      overflow-y: hidden;
    }

    .screen-character-select--mobile-categories .character-select__category-tabs {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 6px;
      margin-bottom: 8px;
    }

    .screen-character-select--mobile-categories .character-select__right-col {
      overflow-y: auto !important;
    }

    .screen-character-select--mobile-categories .character-select__section-panel[hidden] {
      display: none !important;
    }

    .screen-character-select--inline .character-select__choice-grid--heads,
    .screen-character-select--inline .character-select__choice-grid--eyes {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    }

    .screen-character-select--inline [data-head-button] {
      min-width: var(--character-head-button-min-width, 72px) !important;
      height: var(--character-head-button-height, 84px) !important;
    }

    .screen-character-select--inline [data-head-thumbnail] {
      width: min(100%, var(--character-head-image-width, 86px)) !important;
      height: min(100%, var(--character-head-image-height, 66px)) !important;
      max-width: 100% !important;
      max-height: 100% !important;
    }

    .screen-character-select--inline [data-eyes-button] {
      min-height: var(--character-eye-button-min-height, 64px) !important;
    }

    .screen-character-select--inline [data-eyes-thumbnail] {
      width: var(--character-eye-image-width, 68px) !important;
      height: var(--character-eye-image-height, 42px) !important;
    }

    @media (min-width: ${BREAKPOINT_NARROW}) and (max-width: ${BREAKPOINT_MEDIUM_MAX}) {
      .character-select__columns {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(220px, 280px);
        grid-template-rows: auto minmax(220px, 1fr);
        gap: 16px;
        padding: 0;
        min-height: 0;
        align-items: stretch;
      }

      .character-select__left-col {
        width: auto;
        flex-basis: auto;
        grid-column: 1;
        grid-row: 1;
      }

      .character-select__preview-col {
        grid-column: 1;
        grid-row: 2;
        min-width: 0;
        min-height: 220px;
      }

      .character-select__right-col {
        grid-column: 2;
        grid-row: 1 / span 2;
        width: auto;
        max-height: calc(100vh - 132px);
      }
    }

    @media (max-height: 820px) and (min-width: ${BREAKPOINT_NARROW}) {
      .screen-character-select--inline {
        --character-right-col-gap: 8px;
        --character-section-padding: 9px;
        --character-section-gap: 8px;
        --character-head-button-height: 58px;
        --character-head-image-width: 60px;
        --character-head-image-height: 46px;
        --character-eye-button-min-height: 44px;
        --character-eye-image-width: 48px;
        --character-eye-image-height: 30px;
      }
    }

    @media (max-height: 620px), (max-width: 719px) {
      .screen-character-select--inline .character-select__right-col {
        overflow-y: auto !important;
      }
    }

    @container (max-width: 807px) {
      .character-select__columns {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(220px, 280px);
        grid-template-rows: auto minmax(220px, 1fr);
        gap: 16px;
        padding: 0;
        min-height: 0;
        align-items: stretch;
      }

      .character-select__left-col {
        width: auto;
        flex-basis: auto;
        grid-column: 1;
        grid-row: 1;
      }

      .character-select__preview-col {
        grid-column: 1;
        grid-row: 2;
        min-width: 0;
        min-height: 220px;
      }

      .character-select__right-col {
        grid-column: 2;
        grid-row: 1 / span 2;
        width: auto;
        max-height: calc(100vh - 132px);
      }
    }

    @media (max-width: 719px) {
      .screen-character-select {
        gap: 16px !important;
        padding: 16px !important;
        height: auto !important;
        min-height: 100%;
      }

      .screen-character-select [data-title] {
        font-size: clamp(30px, 9vw, 48px) !important;
      }

      .character-select__columns {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 0;
        min-height: 0;
        align-items: stretch;
      }

      .character-select__left-col,
      .character-select__right-col {
        width: 100%;
        flex-basis: auto;
        max-height: calc(100vh - 96px);
      }

      .screen-character-select--inline {
        overflow-y: auto !important;
        overscroll-behavior: contain;
      }

      .screen-character-select--inline .character-select__right-col {
        max-height: none;
        overflow-y: visible !important;
      }

      .character-select__preview-col {
        width: 100%;
        min-width: 0;
        min-height: 240px;
        height: min(42vh, 320px);
        flex: 0 0 auto;
      }

      .character-select__section-panel {
        max-height: none;
      }
    }
  `;
  document.head.appendChild(styleEl);
}

// ─────────────────────────────────────────────────────────────────────────────
// Shape-icon glyphs (one per ColorId — accessibility shape coding for color-blind users)
// Order matches docs/design/screen-details/pre-game.md §Screen 6 + tokens.json color.player.*.
// Shape glyphs are text placeholders until Plan 9 wires actual sprite assets.
// ─────────────────────────────────────────────────────────────────────────────

const COLOR_SHAPE_GLYPHS: Record<ColorId, string> = {
  red: '▲', // ▲ triangle
  blue: '●', // ● circle
  green: '■', // ■ square
  yellow: '◆', // ◆ diamond
  orange: '★', // ★ star
  purple: '♥', // ♥ heart
  pink: '✚', // ✚ plus
  cyan: '⬢', // ⬢ hexagon
  white: '▼', // ▼ inverted triangle
  black: '◐', // ◐ half-circle
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Convert a number hex (0xRRGGBB) to a CSS hex string '#RRGGBB'. */
function hexToCss(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createCharacterSelectScreen(
  ctx: ScreenMountContext<CharacterSelectPayload>,
): ScreenHandle {
  const { gsapRegistry, host, payload, router } = ctx;
  const { adapter, takenColors, onReady } = payload;
  const isInline = payload.mode === 'inline';
  const layoutMode = payload.layoutMode ?? 'desktopRail';
  const usesMobileCategories = layoutMode === 'mobileCategories';
  const nextScreenId: ScreenId = payload.nextScreenId ?? 'lobby-host';

  let disposed = false;
  injectCharacterSelectResponsiveStyles();

  // ── Resolve adapter inventory ────────────────────────────────────────────
  const inv = adapter.inventory();
  const takenSet = new Set<ColorId>(takenColors);

  // ── Resolve initial customization ─────────────────────────────────────────
  // Default: first non-taken color, head 'horse', eyes 'dots', cape 'none'
  // (cape: 'none' is ALWAYS the default per spec §9 line 669).
  function resolveInitial(): CharacterCustomization {
    if (payload.initial) return payload.initial;

    // First non-taken color from inventory (fallback to first inventory color if all taken)
    const firstAvailableColor = inv.colors.find((c) => !takenSet.has(c)) ?? inv.colors[0];
    if (firstAvailableColor === undefined) {
      throw new Error('[character-select] adapter.inventory().colors is empty.');
    }

    return {
      color: firstAvailableColor,
      head: 'horse',
      eyes: 'dots',
      cape: 'none',
    };
  }

  // Mutable customization state — drives the rig's applyCustomization on every UI change
  const customization: CharacterCustomization = { ...resolveInitial() };
  let isReady = payload.ready === true;

  // ── Build rig ONCE ────────────────────────────────────────────────────────
  const rig: CharacterPreviewRig = adapter.buildRig(customization);

  // Track button handles for cleanup
  const buttonHandles: UIComponentHandle[] = [];

  // ── Root element ──────────────────────────────────────────────────────────

  const rootEl = document.createElement('div');
  rootEl.className = [
    'screen-character-select',
    isInline ? 'screen-character-select--inline' : '',
    usesMobileCategories ? 'screen-character-select--mobile-categories' : '',
  ]
    .filter(Boolean)
    .join(' ');
  rootEl.style.cssText = isInline
    ? [
        'display: flex',
        'flex-direction: column',
        'align-items: stretch',
        'justify-content: flex-start',
        'gap: 0',
        'width: 100%',
        'height: 100%',
        'min-height: 0',
        'padding: 0',
        'box-sizing: border-box',
        'overflow: hidden',
      ].join('; ')
    : [
        'display: flex',
        'flex-direction: column',
        'align-items: center',
        'justify-content: flex-start',
        'gap: 24px',
        'width: 100%',
        'padding: 32px 24px',
        'box-sizing: border-box',
      ].join('; ');

  // ── Title ─────────────────────────────────────────────────────────────────

  const titleEl = document.createElement('h1');
  titleEl.setAttribute('data-title', '');
  titleEl.textContent = 'CHOOSE YOUR LOOK';
  titleEl.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    `font-size: ${FONT_SIZE_H1}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    'font-weight: 700',
    `text-shadow: ${TITLE_SHADOW}`,
    'letter-spacing: 1px',
    'margin: 0',
    'text-align: center',
  ].join('; ');
  if (!isInline) {
    rootEl.appendChild(titleEl);
  }

  // ── Three-column row (ready/summary | preview | customization) ────────────

  const columnsRow = document.createElement('div');
  columnsRow.className = 'character-select__columns';
  if (!isInline) {
    rootEl.appendChild(columnsRow);
  }

  // Helper — build a labeled section panel (used for COLOR / HEAD / EYES / CAPE)
  function buildSectionPanel(
    label: string,
    opts?: { width?: string },
  ): {
    panel: HTMLElement;
    body: HTMLElement;
  } {
    const panel = document.createElement('div');
    panel.className = 'character-select__section-panel';
    panel.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'gap: var(--character-section-gap, 12px)',
      'padding: var(--character-section-padding, 16px)',
      `background: ${PANEL_BG}`,
      `border: ${PANEL_BORDER}`,
      `border-radius: ${BORDER_RADIUS_LG}`,
      `box-shadow: ${PANEL_SHADOW}`,
      'box-sizing: border-box',
      opts?.width ? `width: ${opts.width}` : '',
    ]
      .filter(Boolean)
      .join('; ');

    const labelEl = document.createElement('div');
    labelEl.textContent = label;
    labelEl.style.cssText = [
      `font-family: ${FONT_HEADLINE}`,
      `color: ${COLOR_TEXT_PRIMARY}`,
      'font-size: var(--typography-size-h5, 18px)',
      'font-weight: 700',
      'letter-spacing: 2px',
      'text-align: center',
    ].join('; ');
    panel.appendChild(labelEl);

    const body = document.createElement('div');
    panel.appendChild(body);

    return { panel, body };
  }

  // ── Left column: READY + lobby summary ────────────────────────────────────

  let readyHandle: UIComponentHandle | null = null;
  let readyBadgeEl: HTMLElement | null = null;

  if (!isInline) {
    const leftCol = document.createElement('div');
    leftCol.className = 'character-select__left-col';
    columnsRow.appendChild(leftCol);

    const readyPanel = buildSectionPanel('PLAYER', { width: '100%' });
    readyPanel.body.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'gap: 12px',
      'align-items: stretch',
    ].join('; ');
    leftCol.appendChild(readyPanel.panel);

    const playerSummaryEl = document.createElement('div');
    playerSummaryEl.textContent = payload.playerName ?? 'Local Player';
    playerSummaryEl.style.cssText = [
      `font-family: ${FONT_HEADLINE}`,
      `color: ${COLOR_TEXT_PRIMARY}`,
      'font-size: var(--typography-size-h5, 18px)',
      'font-weight: 700',
      'text-align: center',
      'overflow: hidden',
      'text-overflow: ellipsis',
      'white-space: nowrap',
    ].join('; ');
    readyPanel.body.appendChild(playerSummaryEl);

    const roomSummaryEl = document.createElement('div');
    roomSummaryEl.textContent = `Room ${payload.roomCode ?? '----'}`;
    roomSummaryEl.style.cssText = [
      `font-family: var(--typography-family-body, 'Nunito Sans', sans-serif)`,
      `color: ${COLOR_TEXT_SECONDARY}`,
      'font-size: var(--typography-size-caption, 14px)',
      'font-weight: 700',
      'text-align: center',
    ].join('; ');
    readyPanel.body.appendChild(roomSummaryEl);

    readyBadgeEl = document.createElement('div');
    readyBadgeEl.setAttribute('data-ready-indicator', '');
    readyBadgeEl.style.cssText = buildReadyBadgeStyle(isReady);
    readyBadgeEl.textContent = isReady ? 'READY ✓' : 'NOT READY';
    readyPanel.body.appendChild(readyBadgeEl);

    const readyContainer = document.createElement('div');
    readyContainer.style.cssText = ['display: flex', 'justify-content: center'].join('; ');
    readyPanel.body.appendChild(readyContainer);

    readyHandle = createButton({
      gsapRegistry,
      mount: readyContainer,
      variant: isReady ? 'secondary' : 'primary',
      size: 'large',
      label: isReady ? 'UNREADY' : 'READY',
      onClick: () => {
        if (disposed) return;
        isReady = !isReady;
        updateReadyUi();
        const snapshot: CharacterCustomization = { ...customization };
        onReady(snapshot, isReady);
        router.navigate(nextScreenId);
      },
    });
    readyHandle.element.setAttribute('data-action', 'ready');
    readyHandle.element.style.width = '100%';
    buttonHandles.push(readyHandle);
  } else {
    void onReady;
  }

  const colorSwatchEls = new Map<ColorId, HTMLElement>();

  // ── Center column: 3D Preview canvas host ─────────────────────────────────

  const previewCol = document.createElement('div');
  previewCol.className = 'character-select__preview-col';
  previewCol.setAttribute('data-preview-host', '');
  if (!isInline) {
    columnsRow.appendChild(previewCol);
  }

  // Canvas host element — Plan 19's main loop will mount the renderer here.
  const canvasEl = document.createElement('canvas');
  canvasEl.setAttribute('data-preview-canvas', '');
  canvasEl.style.cssText = ['width: 100%', 'height: 100%', 'display: block'].join('; ');
  if (!isInline) {
    previewCol.appendChild(canvasEl);
  }

  // ── Right column: customization (HEAD / EYES / COLOR / CAPE) ─────────────

  const rightCol = document.createElement('div');
  rightCol.className = 'character-select__right-col';
  if (isInline) {
    rightCol.style.width = '100%';
    rightCol.style.height = '100%';
    rightCol.style.maxHeight = '100%';
    rightCol.style.minHeight = '0';
    rightCol.style.flex = '1 1 auto';
    rightCol.style.alignSelf = 'stretch';
    rightCol.style.overflowY = 'hidden';
    rootEl.appendChild(rightCol);
  } else {
    columnsRow.appendChild(rightCol);
  }

  type CategoryId = 'head' | 'eyes' | 'cape' | 'color';
  const categoryPanels = new Map<CategoryId, HTMLElement>();
  const categoryButtons = new Map<CategoryId, HTMLButtonElement>();
  let activeCategory: CategoryId = 'head';

  const categoryTabs = document.createElement('div');
  categoryTabs.className = 'character-select__category-tabs';
  if (usesMobileCategories) {
    rightCol.appendChild(categoryTabs);
  }

  function categoryCameraView(category: CategoryId): CharacterCustomizeCameraView {
    return category === 'cape' ? 'back' : 'front';
  }

  function emitDesktopSelectionCameraHint(category: CategoryId): void {
    if (usesMobileCategories) return;
    payload.onCustomizeCamera?.(categoryCameraView(category));
  }

  function setPanelFocusable(panel: HTMLElement, focusable: boolean): void {
    const controls = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]',
    );
    for (const control of controls) {
      if (focusable) {
        if (control.getAttribute('data-prev-tabindex') != null) {
          const previous = control.getAttribute('data-prev-tabindex');
          control.removeAttribute('data-prev-tabindex');
          if (previous === '') control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', previous!);
        } else {
          control.removeAttribute('tabindex');
        }
      } else if (control.getAttribute('tabindex') !== '-1') {
        control.setAttribute('data-prev-tabindex', control.getAttribute('tabindex') ?? '');
        control.setAttribute('tabindex', '-1');
      }
    }
  }

  function syncMobileCategoryVisibility(emitCameraHint: boolean): void {
    if (!usesMobileCategories) return;
    for (const [category, panel] of categoryPanels.entries()) {
      const active = category === activeCategory;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', String(!active));
      setPanelFocusable(panel, active);
    }
    for (const [category, button] of categoryButtons.entries()) {
      const selected = category === activeCategory;
      button.setAttribute('aria-selected', String(selected));
      button.style.borderColor = selected ? COLOR_AMBER : 'rgba(255,255,255,0.18)';
      button.style.background = selected ? 'rgba(232,160,32,0.22)' : 'rgba(255,255,255,0.08)';
    }
    if (emitCameraHint) payload.onCustomizeCamera?.(categoryCameraView(activeCategory));
  }

  function registerCategory(category: CategoryId, label: string, panel: HTMLElement): void {
    categoryPanels.set(category, panel);
    if (!usesMobileCategories) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.setAttribute('role', 'tab');
    button.setAttribute('data-category-tab', category);
    button.style.cssText = [
      'min-height: 38px',
      'border-radius: 9999px',
      'border: 2px solid rgba(255,255,255,0.18)',
      'background: rgba(255,255,255,0.08)',
      `color: ${COLOR_TEXT_PRIMARY}`,
      `font-family: ${FONT_HEADLINE}`,
      'font-size: 12px',
      'font-weight: 700',
      'padding: 7px 8px',
      'cursor: pointer',
      'box-sizing: border-box',
    ].join('; ');
    button.addEventListener('click', () => {
      if (disposed) return;
      activeCategory = category;
      syncMobileCategoryVisibility(true);
    });
    categoryTabs.appendChild(button);
    categoryButtons.set(category, button);
  }

  // ── HEAD section ──────────────────────────────────────────────────────────

  const headPanel = buildSectionPanel('HEAD');
  registerCategory('head', 'Head', headPanel.panel);
  headPanel.body.className = 'character-select__choice-grid character-select__choice-grid--heads';
  headPanel.body.style.cssText = [
    'display: grid',
    'grid-template-columns: repeat(2, 1fr)',
    `gap: ${SPACING_SM}`,
  ].join('; ');
  rightCol.appendChild(headPanel.panel);

  const headBtnEls = new Map<HeadId, HTMLElement>();
  for (const headId of inv.heads) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-head-button', headId);
    btn.style.cssText = buildHeadPreviewButtonStyle(customization.head === headId);
    btn.appendChild(buildHeadPreviewTile(headId));
    btn.addEventListener('click', () => {
      if (disposed) return;
      selectHead(headId);
    });
    headPanel.body.appendChild(btn);
    headBtnEls.set(headId, btn);
  }

  // ── EYES section ──────────────────────────────────────────────────────────

  const eyesPanel = buildSectionPanel('EYES');
  registerCategory('eyes', 'Eyes', eyesPanel.panel);
  eyesPanel.body.className = 'character-select__choice-grid character-select__choice-grid--eyes';
  eyesPanel.body.style.cssText = [
    'display: grid',
    'grid-template-columns: repeat(2, 1fr)',
    `gap: ${SPACING_SM}`,
  ].join('; ');
  rightCol.appendChild(eyesPanel.panel);

  const eyesBtnEls = new Map<EyeId, HTMLElement>();
  for (const eyeId of inv.eyes) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-eyes-button', eyeId);
    btn.style.cssText = buildIconButtonStyle(customization.eyes === eyeId);
    btn.appendChild(buildEyePreviewTile(eyeId));
    btn.addEventListener('click', () => {
      if (disposed) return;
      selectEyes(eyeId);
    });
    eyesPanel.body.appendChild(btn);
    eyesBtnEls.set(eyeId, btn);
  }

  // ── COLOR section ────────────────────────────────────────────────────────

  const colorPanel = buildSectionPanel('COLOR');
  registerCategory('color', 'Color', colorPanel.panel);
  colorPanel.body.className = 'character-select__choice-grid character-select__choice-grid--colors';
  colorPanel.body.style.cssText = [
    'display: grid',
    'grid-template-columns: repeat(5, 1fr)',
    `gap: ${SPACING_SM}`,
    'justify-items: center',
    'align-items: center',
  ].join('; ');
  rightCol.appendChild(colorPanel.panel);

  // Build the 10 color swatches below EYES and above CAPE.
  for (const colorId of inv.colors) {
    const cellEl = document.createElement('div');
    cellEl.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 2px',
    ].join('; ');

    const swatch = document.createElement('button');
    swatch.type = 'button';
    swatch.setAttribute('data-color-swatch', colorId);
    const isTaken = takenSet.has(colorId);
    swatch.style.cssText = [
      'width: 44px',
      'height: 44px',
      'border-radius: 50%',
      `background: ${hexToCss(COLOR_ID_TO_HEX[colorId])}`,
      `border: 3px solid ${customization.color === colorId ? COLOR_AMBER : 'rgba(0,0,0,0.40)'}`,
      'cursor: pointer',
      'padding: 0',
      'box-sizing: border-box',
      `opacity: ${isTaken ? '0.35' : '1'}`,
      `pointer-events: ${isTaken ? 'none' : 'auto'}`,
      'transition: border-color 150ms cubic-bezier(0.2,0,0,1)',
    ].join('; ');
    if (isTaken) {
      swatch.setAttribute('aria-disabled', 'true');
    }
    swatch.setAttribute('aria-label', `Color ${colorId}`);

    const glyphEl = document.createElement('span');
    glyphEl.textContent = COLOR_SHAPE_GLYPHS[colorId];
    glyphEl.style.cssText = [
      `color: ${COLOR_TEXT_SECONDARY}`,
      'font-size: 11px',
      'line-height: 1',
      'pointer-events: none',
      'user-select: none',
    ].join('; ');

    swatch.addEventListener('click', () => {
      if (disposed) return;
      if (isTaken) return;
      selectColor(colorId);
    });

    cellEl.appendChild(swatch);
    cellEl.appendChild(glyphEl);
    colorPanel.body.appendChild(cellEl);
    colorSwatchEls.set(colorId, swatch);
  }

  // ── CAPE section ──────────────────────────────────────────────────────────

  const capePanel = buildSectionPanel('CAPE');
  registerCategory('cape', 'Cape', capePanel.panel);
  capePanel.body.className = 'character-select__choice-grid character-select__choice-grid--capes';
  capePanel.body.style.cssText = [
    'display: flex',
    'flex-wrap: wrap',
    `gap: ${SPACING_SM}`,
    'justify-content: center',
    'align-items: center',
  ].join('; ');
  rightCol.appendChild(capePanel.panel);

  const capeBtnEls = new Map<CapeId, HTMLElement>();
  for (const capeId of inv.capes) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-cape-button', capeId);
    btn.setAttribute('aria-label', `Cape ${capeId}`);
    btn.style.cssText = buildCapeCircleStyle(capeId, customization.cape === capeId);
    if (capeId === 'none') {
      btn.textContent = '⊘';
    }
    btn.addEventListener('click', () => {
      if (disposed) return;
      selectCape(capeId);
    });
    capePanel.body.appendChild(btn);
    capeBtnEls.set(capeId, btn);
  }

  // Desktop rail order: Head, Eyes, Cape, Color. The color section is built
  // before cape so swatch maps are available, then moved to the final slot.
  rightCol.appendChild(colorPanel.panel);
  syncMobileCategoryVisibility(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Selection handlers
  // ─────────────────────────────────────────────────────────────────────────

  function selectColor(colorId: ColorId): void {
    customization.color = colorId;
    // Refresh swatch borders
    for (const [id, el] of colorSwatchEls.entries()) {
      el.style.borderColor = id === colorId ? COLOR_AMBER : 'rgba(0,0,0,0.40)';
    }
    refreshHeadPreviewTiles();
    rig.applyCustomization({ ...customization });
    payload.onSelect?.({ ...customization });
    emitDesktopSelectionCameraHint('color');
  }

  function selectHead(headId: HeadId): void {
    customization.head = headId;
    for (const [id, el] of headBtnEls.entries()) {
      el.style.cssText = buildHeadPreviewButtonStyle(id === headId);
    }
    rig.applyCustomization({ ...customization });
    payload.onSelect?.({ ...customization });
    emitDesktopSelectionCameraHint('head');
  }

  function refreshHeadPreviewTiles(): void {
    for (const [id, el] of headBtnEls.entries()) {
      el.replaceChildren(buildHeadPreviewTile(id));
    }
  }

  function updateReadyUi(): void {
    if (readyBadgeEl == null || readyHandle == null) return;
    readyBadgeEl.textContent = isReady ? 'READY ✓' : 'NOT READY';
    readyBadgeEl.style.cssText = buildReadyBadgeStyle(isReady);
    const label = readyHandle.element.querySelector('span');
    if (label) label.textContent = isReady ? 'UNREADY' : 'READY';
  }

  function selectEyes(eyeId: EyeId): void {
    customization.eyes = eyeId;
    for (const [id, el] of eyesBtnEls.entries()) {
      el.style.cssText = buildIconButtonStyle(id === eyeId);
    }
    rig.applyCustomization({ ...customization });
    payload.onSelect?.({ ...customization });
    emitDesktopSelectionCameraHint('eyes');
  }

  function selectCape(capeId: CapeId): void {
    customization.cape = capeId;
    for (const [id, el] of capeBtnEls.entries()) {
      el.style.cssText = buildCapeCircleStyle(id, id === capeId);
      el.textContent = id === 'none' ? '⊘' : '';
    }
    rig.applyCustomization({ ...customization });
    payload.onSelect?.({ ...customization });
    emitDesktopSelectionCameraHint('cape');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Apply initial customization to the rig (so customizationsApplied[0] reflects mount state)
  // ─────────────────────────────────────────────────────────────────────────

  rig.applyCustomization({ ...customization });

  // ── Mount ─────────────────────────────────────────────────────────────────

  host.appendChild(rootEl);

  // ─────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const h of buttonHandles) {
      h.dispose();
    }
    buttonHandles.length = 0;
    rig.dispose();
    if (rootEl.parentNode) {
      rootEl.parentNode.removeChild(rootEl);
    }
  }

  return {
    element: rootEl,
    dispose,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Local style helpers (icon button + cape circle)
// ─────────────────────────────────────────────────────────────────────────────

function buildReadyBadgeStyle(ready: boolean): string {
  return [
    'align-self: center',
    'border-radius: 9999px',
    'padding: 4px 10px',
    `background: ${ready ? COLOR_SUCCESS : 'rgba(255,255,255,0.08)'}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    `border: 1px solid ${ready ? '#2E5E1C' : 'rgba(255,255,255,0.16)'}`,
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 11px',
    'font-weight: 700',
    'letter-spacing: 1px',
    'line-height: 1.3',
  ].join('; ');
}

function buildHeadPreviewButtonStyle(selected: boolean): string {
  return [
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'min-width: 72px',
    'height: 84px',
    'padding: 2px',
    'background: rgba(255, 255, 255, 0.08)',
    `border: 2px solid ${selected ? COLOR_AMBER : 'rgba(255, 255, 255, 0.20)'}`,
    'border-radius: 8px',
    'cursor: pointer',
    'box-sizing: border-box',
    'overflow: hidden',
    'min-height: 0',
    'transition: border-color 150ms cubic-bezier(0.2,0,0,1), box-shadow 150ms cubic-bezier(0.2,0,0,1)',
    selected ? `box-shadow: 0 0 0 2px ${COLOR_AMBER} inset` : 'box-shadow: none',
  ].join('; ');
}

function buildHeadPreviewTile(headId: HeadId): HTMLElement {
  const tile = document.createElement('div');
  tile.setAttribute('data-head-preview-tile', headId);
  tile.style.cssText = [
    'width: 100%',
    'height: 100%',
    'min-width: 0',
    'min-height: 0',
    'display: grid',
    'place-items: center',
    'overflow: hidden',
    'pointer-events: none',
  ].join('; ');

  const image = document.createElement('img');
  image.src = headMenuThumbnailUrl(headId);
  image.alt = '';
  image.draggable = false;
  image.setAttribute('data-head-thumbnail', headId);
  image.style.cssText = [
    'width: 86px',
    'height: 66px',
    'display: block',
    'object-fit: contain',
    'object-position: center center',
    'pointer-events: none',
    'filter: drop-shadow(0 2px 0 rgba(0,0,0,0.55))',
  ].join('; ');
  image.addEventListener(
    'error',
    () => {
      const fallback = document.createElement('span');
      fallback.textContent = HEAD_PREVIEW_GLYPHS[headId];
      fallback.style.cssText = [
        'font-size: 24px',
        'line-height: 1',
        'align-self: center',
        'text-shadow: 0 2px 0 #000000',
        'pointer-events: none',
      ].join('; ');
      image.replaceWith(fallback);
    },
    { once: true },
  );
  tile.appendChild(image);

  return tile;
}

function buildEyePreviewTile(eyeId: EyeId): HTMLElement {
  const tile = document.createElement('div');
  tile.style.cssText = [
    'display: grid',
    'align-items: center',
    'justify-content: center',
    'width: 100%',
    'height: 100%',
    'min-height: 0',
  ].join('; ');

  const image = document.createElement('img');
  image.src = eyeMenuThumbnailUrl(eyeId);
  image.alt = formatLabel(eyeId);
  image.draggable = false;
  image.setAttribute('data-eyes-thumbnail', eyeId);
  image.setAttribute('data-eye-preview-url', eyeMenuThumbnailUrl(eyeId));
  image.style.cssText = [
    'width: 68px',
    'height: 42px',
    'display: block',
    'object-fit: contain',
    'image-rendering: auto',
    'pointer-events: none',
    'filter: drop-shadow(0 1px 0 rgba(0,0,0,0.65))',
  ].join('; ');
  tile.appendChild(image);
  return tile;
}

function headMenuThumbnailUrl(headId: HeadId): string {
  const fileId = headId.replaceAll('-', '_');
  return `/assets/textures/character-head/menu/head_${fileId}.webp`;
}

function eyeMenuThumbnailUrl(eyeId: EyeId): string {
  const dir = eyeId === 'x-eyes' ? 'xeyes' : eyeId;
  return `/assets/textures/eyes/menu/${dir}.webp`;
}

const HEAD_PREVIEW_GLYPHS: Record<HeadId, string> = {
  horse: '♞',
  chicken: '◆',
  'cardboard-box': '▣',
  shark: '▲',
  pumpkin: '●',
  alien: '◉',
  unicorn: '★',
  'traffic-cone': '△',
};

/** Build the inline CSS for HEAD / EYES grid buttons. */
function buildIconButtonStyle(selected: boolean): string {
  return [
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'min-height: 64px',
    'padding: 6px 8px',
    'background: rgba(255, 255, 255, 0.08)',
    `color: ${COLOR_TEXT_PRIMARY}`,
    `border: 2px solid ${selected ? COLOR_AMBER : 'rgba(255, 255, 255, 0.20)'}`,
    'border-radius: 8px',
    'cursor: pointer',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 11px',
    'font-weight: 700',
    'letter-spacing: 0.5px',
    'text-transform: uppercase',
    'text-align: center',
    'box-sizing: border-box',
    selected ? `box-shadow: 0 0 0 2px ${COLOR_AMBER} inset` : 'box-shadow: none',
    'transition: border-color 150ms cubic-bezier(0.2,0,0,1), box-shadow 150ms cubic-bezier(0.2,0,0,1)',
  ].join('; ');
}

/** Build the inline CSS for a CAPE circle button. */
function buildCapeCircleStyle(capeId: CapeId, selected: boolean): string {
  const textureUrl = capeId === 'none' ? null : `/assets/textures/capes/${capeId}.webp`;

  return [
    'width: 44px',
    'height: 44px',
    'border-radius: 8px',
    textureUrl == null
      ? 'background: rgba(255,255,255,0.05)'
      : `background: rgba(255,255,255,0.08) url("${textureUrl}") center / cover no-repeat`,
    `border: 2px solid ${selected ? COLOR_AMBER : 'rgba(0,0,0,0.40)'}`,
    'cursor: pointer',
    'padding: 0',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    `color: ${COLOR_TEXT_PRIMARY}`,
    'font-size: 18px',
    'line-height: 1',
    'box-sizing: border-box',
    selected ? `box-shadow: 0 0 0 2px ${COLOR_AMBER} inset` : 'box-shadow: none',
    'transition: border-color 150ms cubic-bezier(0.2,0,0,1)',
  ].join('; ');
}

/** Convert an id like "cardboard-box" or "x-eyes" → "Cardboard Box" / "X-Eyes" for display. */
function formatLabel(id: string): string {
  return id
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
